import { useState, useMemo, useEffect, useRef } from 'react'
import { buildUomRows, urgencyFromRows, parseTraxxallDate, buildActualsMap, isAndLogicTask, stripCalendarForAndLogic, fetchEbisListing, getDiscrepItemsForReg, getCachedWoDiscrepItems, computeItemPlanStatus } from './TraxxallDueList'
import TraxxallDueList from './TraxxallDueList'
import { isOverviewItem } from '../lib/overviewTasks'
import { legsTooltip } from '../lib/flightUtils'

// ─── Date helpers ────────────────────────────────────────────────────────────

function toYMD(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function todayYMD() {
  return toYMD(new Date())
}

// Build a 4–6 row × 7 column array of Date objects for the month grid.
// Rows start on Sunday of the week that contains the 1st.
function buildCalendarWeeks(year, month) {
  const firstDay = new Date(year, month, 1)
  const startDow = firstDay.getDay()  // 0 = Sunday
  const weeks    = []
  let y = year, m = month, d = 1 - startDow
  while (true) {
    const week = []
    for (let i = 0; i < 7; i++) {
      week.push(new Date(y, m, d))
      d++
    }
    weeks.push(week)
    if (new Date(y, m, d) > new Date(y, month + 1, 0)) break
    if (weeks.length >= 6) break
  }
  return weeks
}

// ─── Urgency helpers ─────────────────────────────────────────────────────────

const URGENCY_RANK = { overdue: 4, 'tol-expiry': 3.5, 'soft-overdue': 3, warning: 2, ok: 1 }

function worstUrgency(entries) {
  let best = 'ok'
  for (const e of entries) {
    if ((URGENCY_RANK[e.urgency] || 0) > (URGENCY_RANK[best] || 0)) best = e.urgency
  }
  return best
}

function cellBg(urgency) {
  switch (urgency) {
    case 'overdue':      return 'bg-red-50 hover:bg-red-100 cursor-pointer'
    case 'tol-expiry':   return 'bg-red-50 hover:bg-red-100 cursor-pointer'
    case 'soft-overdue': return 'bg-orange-50 hover:bg-orange-100 cursor-pointer'
    case 'warning':      return 'bg-yellow-50 hover:bg-yellow-100 cursor-pointer'
    case 'ok':           return 'bg-green-50 hover:bg-green-100 cursor-pointer'
    default:             return 'hover:bg-gray-50 cursor-default'
  }
}

const URGENCY_DOT = {
  'overdue':      'bg-red-500',
  'tol-expiry':   'bg-red-400',
  'soft-overdue': 'bg-orange-400',
  'warning':      'bg-yellow-400',
  'ok':           'bg-green-400',
}

const URGENCY_PILL = {
  'overdue':      'bg-red-100 text-red-700',
  'tol-expiry':   'bg-red-100 text-red-700',
  'soft-overdue': 'bg-orange-100 text-orange-700',
  'warning':      'bg-yellow-100 text-yellow-700',
  'ok':           'bg-green-100 text-green-700',
}

// ─── Plan status icon ─────────────────────────────────────────────────────────

const PLAN_STATUS_CFG = {
  complete:  { path: 'M5 13l4 4L19 7',                                                                                                                                                   sw: 2.5, cls: 'text-green-500',  label: 'Complete'  },
  review:    { path: 'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',   sw: 2,   cls: 'text-gray-400',   label: 'Review'    },
  'in-work': { path: 'M21.75 6.75a4.5 4.5 0 01-4.884 4.484c-1.076-.091-2.264.071-2.95.904l-7.152 8.684a2.548 2.548 0 11-3.586-3.586l8.684-7.152c.833-.686.995-1.874.904-2.95a4.5 4.5 0 016.336-4.486l-3.276 3.276a3.004 3.004 0 002.25 2.25l3.276-3.276c.256.565.398 1.192.398 1.852z', sw: 2, cls: 'text-blue-500',  label: 'In Work'   },
  planned:   { path: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',                                                                                                                     sw: 2,   cls: 'text-yellow-500', label: 'Planned'   },
  unplanned: { path: 'M6 18L18 6M6 6l12 12',                                                                                                                                             sw: 2.5, cls: 'text-red-400',    label: 'Unplanned' },
}

function PlanStatusIcon({ status }) {
  const cfg = PLAN_STATUS_CFG[status]
  if (!cfg) return null
  return (
    <span className={`flex-shrink-0 flex flex-col items-center gap-0.5 ${cfg.cls}`} title={cfg.label}>
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={cfg.sw} d={cfg.path} />
      </svg>
      <span className="text-[9px] font-semibold leading-none">{cfg.label}</span>
    </span>
  )
}

// ─── Data helpers ─────────────────────────────────────────────────────────────

const HOURS_CYCLE_UOMS = new Set(['Hrs', 'Ldg', 'Enc', 'Prc', 'Ach', 'Flcy'])

// Effective calendar date: sooner of SchedAero projection, calendar hard due, Traxxall estimate.
// Ensures hours-only items (no next Due Date) and items where hours exhaust before the
// calendar limit both land on the correct day.
// AND-logic tasks ignore calDue — only the hours projection governs placement.
function effectiveItemDate(item, proj, reg) {
  const andLogic = isAndLogicTask(item['taskNo'], reg)
  const calDue   = andLogic ? null : parseTraxxallDate(item['next Due Date'])
  const estDue   = parseTraxxallDate(item['estimated Due Date'])
  const projDate = proj?.projDate || null
  if (projDate) {
    if (calDue) return projDate < calDue ? projDate : calDue
    return projDate
  }
  if (calDue && estDue) return estDue < calDue ? estDue : calDue
  return calDue || estDue || null
}

function buildDayMap(allData, registrations, projections = {}, overviewFilter = null, tolExpiryProjections = {}) {
  const map   = {}
  const today = new Date(); today.setHours(0, 0, 0, 0)
  for (const reg of registrations) {
    const d = allData[reg]
    if (!d?.items?.length) continue
    const actualsMap = buildActualsMap(d.actuals)
    const regProjs   = projections[reg]  // Map<item, { projDate, projType }>
    const regTolProjs = tolExpiryProjections[reg]  // Map<item, { projDate, projType }>
    for (const item of d.items) {
      if (overviewFilter && !overviewFilter(item)) continue
      const rawRows = buildUomRows(item, actualsMap)
      const rows    = stripCalendarForAndLogic(rawRows, item['taskNo'], reg)
      const urgency = urgencyFromRows(rows)
      // Hard-overdue items are surfaced in the OVD badge panel; skip the calendar.
      if (urgency === 'overdue') continue

      const proj        = regProjs?.get(item)
      const isProjected = proj?.projDate != null

      // Items in hours/cycles tolerance: show projected tolerance-expiry date on calendar, then skip normal placement.
      const isInHrsTolerance = rows.some(r => !r.isApprox && r.tolRem != null && HOURS_CYCLE_UOMS.has(r.uom))
      if (isInHrsTolerance) {
        const tolProj = regTolProjs?.get(item)
        if (tolProj?.projDate) {
          const key = toYMD(tolProj.projDate)
          if (!map[key]) map[key] = []
          map[key].push({ reg, item, urgency: 'tol-expiry', isProjected: true, projType: tolProj.projType, isTraxxallEstimate: false, isTolExpiry: true })
        }
        continue
      }

      // Soft-overdue (calendar grace period) items only appear if we have a future hours projection.
      if (urgency === 'soft-overdue' && !isProjected) continue

      const date = (isProjected && urgency === 'soft-overdue')
        ? proj.projDate
        : effectiveItemDate(item, proj, reg)
      if (!date) continue

      let displayUrgency = (urgency === 'soft-overdue' && isProjected) ? 'ok' : urgency
      // If the effective due date is within 30 days, always show yellow regardless of
      // remaining hours — the projection date is what matters for scheduling urgency.
      const daysUntil = Math.round((date.getTime() - today.getTime()) / 86_400_000)
      if (daysUntil <= 30 && displayUrgency === 'ok') displayUrgency = 'warning'
      if (daysUntil > 30 && displayUrgency === 'warning') displayUrgency = 'ok'

      // True when the calendar placement date comes solely from Traxxall's estimated due date
      // (no hard next Due Date and no fleet rate projection). The cell text shows ≈ in gray.
      const isTraxxallEstimate = !isProjected && !parseTraxxallDate(item['next Due Date']) && !!parseTraxxallDate(item['estimated Due Date'])

      const key = toYMD(date)
      if (!map[key]) map[key] = []
      map[key].push({ reg, item, urgency: displayUrgency, isProjected, projType: proj?.projType || null, isTraxxallEstimate })
    }
  }
  return map
}

// Build per-aircraft urgency summary across ALL enrolled aircraft (fleet-wide).
// Returns { [reg]: { overdue: Item[] } } — only regs with overdue items included.
function buildUrgencySummary(allData, enrolledAircraft, isAddressedFn = null, overviewFilter = null) {
  const summary = {}
  for (const a of enrolledAircraft) {
    const reg = a.registration
    const d   = allData[reg]
    if (!d?.items?.length) continue
    const actualsMap  = buildActualsMap(d.actuals)
    const overdue     = []
    const inTolerance = []
    for (const item of d.items) {
      if (isAddressedFn?.(item, reg)) continue
      if (overviewFilter && !overviewFilter(item)) continue
      const rows = stripCalendarForAndLogic(buildUomRows(item, actualsMap), item['taskNo'], reg)
      if (urgencyFromRows(rows) === 'overdue') overdue.push(item)
      if (rows.some(r => !r.isApprox && r.tolRem != null && HOURS_CYCLE_UOMS.has(r.uom))) inTolerance.push(item)
    }
    if (overdue.length || inTolerance.length) summary[reg] = { overdue, inTolerance }
  }
  return summary
}

// ─── Shared task row ─────────────────────────────────────────────────────────

function TaskRow({ item, dotCls, isTraxxallEstimate, planStatus, suffix }) {
  return (
    <div className="flex items-start gap-2 text-xs text-gray-700 pl-6">
      <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${dotCls}`} />
      <div className="min-w-0 flex-1">
        <span className="font-medium text-gray-900 truncate block">
          {item['description'] || item['taskNo'] || '—'}
          {suffix && <span className="ml-1.5 text-red-500 font-normal italic">({suffix})</span>}
        </span>
        <span className="text-gray-400">
          {[item['task Type'], item['ma'] || 'Airframe'].filter(Boolean).join(' · ')}
        </span>
      </div>
      {planStatus && <PlanStatusIcon status={planStatus} />}
      <span className={`flex-shrink-0 pl-2 ${isTraxxallEstimate ? 'text-gray-400 italic' : 'text-gray-500'}`}>
        {item['remaining Time'] || (item['estimated Due Date'] ? `≈ ${item['estimated Due Date']}` : '')}
      </span>
    </div>
  )
}

// ─── Badge Panel ─────────────────────────────────────────────────────────────
// Shown when the OVD or TOL badge is clicked — lists aircraft + tasks for that urgency.

function BadgePanel({ urgencySummary, mode, viewMode, onClose, onSelectReg, discrepItemsByReg = {}, activeProjectsByReg = {} }) {
  const [openRegs, setOpenRegs] = useState({})

  const acEntries = useMemo(() => {
    const key = mode === 'tol' ? 'inTolerance' : 'overdue'
    return Object.entries(urgencySummary)
      .filter(([, s]) => (s[key] || []).length > 0)
      .map(([reg, s])  => ({ reg, items: s[key] || [] }))
      .sort((a, b) => b.items.length - a.items.length)
  }, [urgencySummary, mode])

  const title   = mode === 'tol' ? 'In Tolerance' : 'Overdue'
  const pillCls = mode === 'tol' ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'
  const dotCls  = mode === 'tol' ? 'bg-orange-400' : 'bg-red-500'

  const toggleReg = (reg) => setOpenRegs(prev => ({ ...prev, [reg]: !prev[reg] }))

  return (
    <div className="border border-gray-200 rounded-xl bg-white shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
        <span className="text-sm font-semibold text-gray-900">{title}</span>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="divide-y divide-gray-100">
        {acEntries.map(({ reg, items }) => {
          const isOpen = !!openRegs[reg]
          return (
            <div key={reg}>
              <button
                onClick={() => toggleReg(reg)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
              >
                <svg
                  className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform duration-150 ${isOpen ? 'rotate-90' : ''}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>

                {viewMode === 'fleet' ? (
                  <span
                    onClick={e => { e.stopPropagation(); onSelectReg(reg) }}
                    className="text-sm font-bold text-brand-red hover:underline"
                  >
                    {reg}
                  </span>
                ) : (
                  <span className="text-sm font-bold text-gray-900">{reg}</span>
                )}

                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${pillCls}`}>
                  {items.length} task{items.length !== 1 ? 's' : ''}
                </span>
                {(activeProjectsByReg[reg] || []).map(p => {
                  const label = p.workscope || [p.work_order, ...(p.work_orders_extra || [])].filter(Boolean).join(' / ') || 'Project'
                  const start = p.arrival_date ? new Date(p.arrival_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null
                  const end   = p.departure_date ? new Date(p.departure_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null
                  const range = [start, end].filter(Boolean).join(' – ')
                  const pillCls = { 'In Work': 'bg-blue-50 text-blue-700 border-blue-100', 'Scheduled': 'bg-gray-100 text-gray-600 border-gray-200', 'TBD': 'bg-yellow-50 text-yellow-700 border-yellow-100' }[p.status] || 'bg-gray-100 text-gray-600 border-gray-200'
                  const dateCls = { 'In Work': 'text-blue-400', 'Scheduled': 'text-gray-400', 'TBD': 'text-yellow-500' }[p.status] || 'text-gray-400'
                  return (
                    <span key={p.id} className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium border whitespace-nowrap ${pillCls}`}>
                      {label}{range && <span className={`font-normal ml-1 ${dateCls}`}>{range}</span>}
                    </span>
                  )
                })}
              </button>

              {isOpen && (
                <div className="px-4 pb-3 space-y-1.5">
                  {items.map((item, i) => {
                    const di = discrepItemsByReg[reg] || []
                    const planStatus = di.length ? computeItemPlanStatus(item, di) : null
                    return <TaskRow key={i} item={item} dotCls={dotCls} planStatus={planStatus} />
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Day Panel ────────────────────────────────────────────────────────────────

function DayPanel({ dateYMD, entries, viewMode, onClose, onSelectReg, discrepItemsByReg = {}, activeProjectsByReg = {} }) {
  const [openRegs, setOpenRegs] = useState({})

  const byReg = useMemo(() => {
    const map = {}
    for (const e of entries) {
      if (!map[e.reg]) map[e.reg] = []
      map[e.reg].push(e)
    }
    return map
  }, [entries])

  useEffect(() => { setOpenRegs({}) }, [dateYMD])

  const toggleReg = (reg) => setOpenRegs(prev => ({ ...prev, [reg]: !prev[reg] }))

  const dateObj   = new Date(dateYMD + 'T00:00:00')
  const formatted = dateObj.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })

  return (
    <div className="border border-gray-200 rounded-xl bg-white shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
        <span className="text-sm font-semibold text-gray-900">{formatted}</span>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="divide-y divide-gray-100">
        {Object.entries(byReg).map(([reg, regEntries]) => {
          const worst  = worstUrgency(regEntries)
          const isOpen = !!openRegs[reg]
          return (
            <div key={reg}>
              <button
                onClick={() => toggleReg(reg)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
              >
                <svg
                  className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform duration-150 ${isOpen ? 'rotate-90' : ''}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>

                {viewMode === 'fleet' ? (
                  <span
                    onClick={e => { e.stopPropagation(); onSelectReg(reg) }}
                    className="text-sm font-bold text-brand-red hover:underline"
                  >
                    {reg}
                  </span>
                ) : (
                  <span className="text-sm font-bold text-gray-900">{reg}</span>
                )}

                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${URGENCY_PILL[worst]}`}>
                  {regEntries.length} task{regEntries.length !== 1 ? 's' : ''}
                </span>
                {(activeProjectsByReg[reg] || []).map(p => {
                  const label = p.workscope || [p.work_order, ...(p.work_orders_extra || [])].filter(Boolean).join(' / ') || 'Project'
                  const start = p.arrival_date ? new Date(p.arrival_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null
                  const end   = p.departure_date ? new Date(p.departure_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null
                  const range = [start, end].filter(Boolean).join(' – ')
                  const pillCls = { 'In Work': 'bg-blue-50 text-blue-700 border-blue-100', 'Scheduled': 'bg-gray-100 text-gray-600 border-gray-200', 'TBD': 'bg-yellow-50 text-yellow-700 border-yellow-100' }[p.status] || 'bg-gray-100 text-gray-600 border-gray-200'
                  const dateCls = { 'In Work': 'text-blue-400', 'Scheduled': 'text-gray-400', 'TBD': 'text-yellow-500' }[p.status] || 'text-gray-400'
                  return (
                    <span key={p.id} className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium border whitespace-nowrap ${pillCls}`}>
                      {label}{range && <span className={`font-normal ml-1 ${dateCls}`}>{range}</span>}
                    </span>
                  )
                })}
              </button>

              {isOpen && (
                <div className="px-4 pb-3 space-y-1.5">
                  {regEntries.map((e, i) => {
                    const di = discrepItemsByReg[e.reg] || []
                    const planStatus = di.length ? computeItemPlanStatus(e.item, di) : null
                    return (
                      <TaskRow key={i} item={e.item} dotCls={URGENCY_DOT[e.urgency] || 'bg-gray-300'} isTraxxallEstimate={e.isTraxxallEstimate} planStatus={planStatus} suffix={e.isTolExpiry ? 'tol. expires' : null} />
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export default function FleetDueCalendar({ enrolledAircraft, allData, regWoMap = {}, woDiscrepByReg = {}, onTaskClick, projections = {}, tolExpiryProjections = {}, isAddressed = null, hideBadges = false, overviewOnly = false, onAddToSchedule, onRegSelect, activeProjectsByReg = {}, flightLegs = [] }) {
  const today = useMemo(() => new Date(), [])

  const [viewMode,     setViewMode]     = useState('fleet')
  const [selectedReg,  setSelectedReg]  = useState(null)
  const [currentMonth, setCurrentMonth] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1))
  const [selectedDate, setSelectedDate] = useState(null)
  const [activeBadge,  setActiveBadge]  = useState(null)  // null | 'ovd' | 'soft-overdue'
  const [regFilter,    setRegFilter]    = useState('')
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const filterRef = useRef(null)

  const [ebisListing, setEbisListing] = useState(null)
  useEffect(() => { fetchEbisListing().then(rows => setEbisListing(rows || [])) }, [])

  // Per-registration discrepancy items (listing + WO details) for plan status icons.
  // Prefer pre-fetched woDiscrepByReg from the parent (always fresh); fall back to the
  // module-level WO cache via getCachedWoDiscrepItems when the parent doesn't supply it.
  const discrepItemsByReg = useMemo(() => {
    if (!ebisListing) return {}
    const map = {}
    for (const a of enrolledAircraft) {
      const reg          = a.registration
      const listingItems = getDiscrepItemsForReg(ebisListing, reg)
      const woItems      = woDiscrepByReg[reg]
        ?? (regWoMap[reg] || []).flatMap(wo => getCachedWoDiscrepItems(wo) || [])
      const woTexts      = new Set(woItems.map(d => d.text))
      map[reg] = [...woItems, ...listingItems.filter(d => !woTexts.has(d.text))]
    }
    return map
  }, [ebisListing, enrolledAircraft, regWoMap, woDiscrepByReg])

  const year  = currentMonth.getFullYear()
  const month = currentMonth.getMonth()

  // Which registrations the calendar grid shows
  const registrations = useMemo(
    () => viewMode === 'single' && selectedReg
      ? [selectedReg]
      : enrolledAircraft.map(a => a.registration),
    [viewMode, selectedReg, enrolledAircraft]
  )

  // Maps JetInsight alt reg → primary app reg (secondary_registration → aircraft.registration)
  const jiToPrimaryReg = useMemo(() => {
    const map = {}
    for (const [primaryReg, projs] of Object.entries(activeProjectsByReg)) {
      for (const p of projs) {
        if (p.secondary_registration) {
          map[p.secondary_registration.trim().toUpperCase()] = primaryReg
          break
        }
      }
    }
    return map
  }, [activeProjectsByReg])

  const flightsByDate = useMemo(() => {
    const regSet = new Set(registrations)
    const map = {}
    for (const leg of flightLegs) {
      const reg = regSet.has(leg.aircraft_reg) ? leg.aircraft_reg : jiToPrimaryReg[leg.aircraft_reg]
      if (!reg) continue
      if (!map[leg.leg_date]) map[leg.leg_date] = []
      map[leg.leg_date].push(leg)
    }
    return map
  }, [flightLegs, registrations, jiToPrimaryReg])

  const awayDates = useMemo(() => {
    const HOME_FBO = 'AAO'
    const regSet = new Set(registrations)
    const dates = new Set()
    const legsByReg = {}
    for (const leg of flightLegs) {
      if (!leg.aircraft_reg) continue
      const reg = regSet.has(leg.aircraft_reg) ? leg.aircraft_reg : jiToPrimaryReg[leg.aircraft_reg]
      if (!reg) continue
      if (!legsByReg[reg]) legsByReg[reg] = []
      legsByReg[reg].push(leg)
    }
    for (const legs of Object.values(legsByReg)) {
      const sorted = [...legs].sort((a, b) => (a.depart_dt||'').localeCompare(b.depart_dt||''))
      for (let i = 0; i < sorted.length - 1; i++) {
        const leg = sorted[i], next = sorted[i + 1]
        if (leg.leg_date === next.leg_date) continue
        if (leg.destination === HOME_FBO || next.origin === HOME_FBO) continue
        const d = new Date(leg.leg_date + 'T00:00:00')
        d.setDate(d.getDate() + 1)
        const end = new Date(next.leg_date + 'T00:00:00')
        while (d < end) {
          dates.add(d.toISOString().slice(0, 10))
          d.setDate(d.getDate() + 1)
        }
      }
    }
    return dates
  }, [flightLegs, registrations, jiToPrimaryReg])

  // Fleet-wide urgency summary — always all enrolled aircraft so badge counts
  // reflect the full fleet even while in single-aircraft view.
  // Recomputes whenever allData changes (e.g. after a Traxxall refresh), so items
  // automatically re-sort into the correct bucket without any manual intervention.
  const urgencySummary = useMemo(
    () => buildUrgencySummary(allData, enrolledAircraft, isAddressed, overviewOnly ? isOverviewItem : null),
    [allData, enrolledAircraft, isAddressed, overviewOnly]
  )

  // In single-aircraft view, scope badges to that aircraft (task count).
  // In fleet view, show aircraft count across the whole fleet.
  const badgeSummary = useMemo(() => {
    if (viewMode === 'single' && selectedReg) {
      const s = urgencySummary[selectedReg]
      return s ? { [selectedReg]: s } : {}
    }
    return urgencySummary
  }, [viewMode, selectedReg, urgencySummary])

  const ovdCount = useMemo(() => {
    const singleReg = (viewMode === 'single' && selectedReg) ? selectedReg
      : enrolledAircraft.length === 1 ? enrolledAircraft[0].registration : null
    if (singleReg) return urgencySummary[singleReg]?.overdue.length || 0
    return Object.values(urgencySummary).filter(s => s.overdue.length > 0).length
  }, [viewMode, selectedReg, urgencySummary, enrolledAircraft])

  const tolCount = useMemo(() => {
    const singleReg = (viewMode === 'single' && selectedReg) ? selectedReg
      : enrolledAircraft.length === 1 ? enrolledAircraft[0].registration : null
    if (singleReg) return urgencySummary[singleReg]?.inTolerance?.length || 0
    return Object.values(urgencySummary).filter(s => (s.inTolerance?.length || 0) > 0).length
  }, [viewMode, selectedReg, urgencySummary, enrolledAircraft])

  // Map of 'YYYY-MM-DD' → [{ reg, item, urgency, isProjected, projType }] for the current view
  const dayMap = useMemo(
    () => buildDayMap(allData, registrations, projections, overviewOnly ? isOverviewItem : null, tolExpiryProjections),
    [allData, registrations, projections, overviewOnly, tolExpiryProjections]
  )

  // String-keyed projection map for TraxxallDueList (which can't use object-reference keys)
  const selectedProjKeyMap = useMemo(() => {
    if (!selectedReg) return {}
    const regMap = projections[selectedReg]
    if (!regMap) return {}
    const result = {}
    for (const [item, proj] of regMap) {
      if (!proj?.projDate) continue
      const k = [item['taskNo'] || '', item['cw Date'] || '', item['ma'] || '', item['position'] || ''].join('\x00')
      result[k] = proj
    }
    return result
  }, [selectedReg, projections])

  const weeks = useMemo(() => buildCalendarWeeks(year, month), [year, month])

  const filteredAircraft = useMemo(() => {
    if (!regFilter.trim()) return enrolledAircraft
    const q = regFilter.trim().toLowerCase()
    return enrolledAircraft.filter(a => a.registration.toLowerCase().includes(q))
  }, [regFilter, enrolledAircraft])

  // Close filter dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return
    const handle = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setDropdownOpen(false)
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [dropdownOpen])

  const anyLoading = useMemo(
    () => registrations.some(r => allData[r]?.loading),
    [registrations, allData]
  )

  // Navigation
  const prevMonth = () => { setCurrentMonth(new Date(year, month - 1, 1)); setSelectedDate(null) }
  const nextMonth = () => { setCurrentMonth(new Date(year, month + 1, 1)); setSelectedDate(null) }
  const goToday   = () => { setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1)); setSelectedDate(null) }

  const handleRegSelect = (reg) => {
    setSelectedReg(reg)
    setViewMode('single')
    setRegFilter(reg)
    setDropdownOpen(false)
    setSelectedDate(null)
    onRegSelect?.(reg)
  }

  const clearRegFilter = () => {
    setSelectedReg(null)
    setViewMode('fleet')
    setRegFilter('')
    setSelectedDate(null)
  }

  const toggleBadge = (key) => setActiveBadge(prev => prev === key ? null : key)

  const monthLabel = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  const todayStr   = todayYMD()

  return (
    <div className="space-y-4">

      {/* Header row — nav + urgency badges + filter */}
      <div className="flex items-center gap-2 flex-wrap">

        {/* Back button — single-aircraft view only */}
        {viewMode === 'single' && (
          <button
            onClick={clearRegFilter}
            className="flex items-center gap-1.5 text-sm text-brand-red hover:text-brand-red-dark font-medium mr-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            All Aircraft
          </button>
        )}

        {/* Month navigation */}
        <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors" aria-label="Previous month">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h2 className="text-sm font-semibold text-gray-900 min-w-[140px] text-center">
          {viewMode === 'single' ? `${selectedReg} — ` : ''}{monthLabel}
        </h2>
        <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors" aria-label="Next month">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
        <button onClick={goToday} className="text-xs px-2.5 py-1 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors">
          Today
        </button>

        {!hideBadges && <div className="w-px h-5 bg-gray-200 mx-1" />}

        {/* Urgency badge strip — hidden when embedded in single-aircraft detail view */}
        {!hideBadges && ovdCount > 0 && (
          <button
            onClick={() => toggleBadge('ovd')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              activeBadge === 'ovd'
                ? 'bg-red-600 text-white'
                : 'bg-red-50 text-red-600 hover:bg-red-100'
            }`}
          >
            {ovdCount} OVD
          </button>
        )}

        {!hideBadges && tolCount > 0 && (
          <button
            onClick={() => toggleBadge('tol')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              activeBadge === 'tol'
                ? 'bg-orange-500 text-white'
                : 'bg-orange-50 text-orange-700 hover:bg-orange-100'
            }`}
          >
            {tolCount} TOL.
          </button>
        )}

        {anyLoading && (
          <span className="text-xs text-gray-400 animate-pulse">Loading data…</span>
        )}

        {/* Registration filter — hidden when only one aircraft is shown */}
        <div ref={filterRef} className={`relative ml-auto${enrolledAircraft.length <= 1 ? ' hidden' : ''}`}>
          <div className="flex items-center gap-1">
            <div className="relative">
              <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                value={regFilter}
                onChange={e => { setRegFilter(e.target.value); setDropdownOpen(true) }}
                onFocus={() => setDropdownOpen(true)}
                placeholder="Filter by tail number…"
                className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg w-52 focus:outline-none focus:ring-2 focus:ring-brand-red/20"
              />
            </div>
            {viewMode === 'single' && (
              <button
                onClick={clearRegFilter}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                title="Clear filter"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {dropdownOpen && filteredAircraft.length > 0 && (
            <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[160px] overflow-hidden">
              {filteredAircraft.map(a => (
                <button
                  key={a.registration}
                  onMouseDown={() => handleRegSelect(a.registration)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-red-50 text-gray-700 hover:text-brand-red transition-colors"
                >
                  <span className="font-medium">{a.registration}</span>
                  {(a.make || a.model) && (
                    <span className="ml-2 text-xs text-gray-400">{[a.make, a.model].filter(Boolean).join(' ')}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Badge panel — shown below header when OVD badge is active */}
      {activeBadge && (
        <BadgePanel
          urgencySummary={badgeSummary}
          mode={activeBadge}
          viewMode={viewMode}
          onClose={() => setActiveBadge(null)}
          onSelectReg={handleRegSelect}
          discrepItemsByReg={discrepItemsByReg}
          activeProjectsByReg={activeProjectsByReg}
        />
      )}

      {/* Calendar grid */}
      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
        <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
          {DOW_LABELS.map(d => (
            <div key={d} className="text-xs font-semibold text-gray-500 text-center py-2.5">{d}</div>
          ))}
        </div>

        {weeks.map((week, wi) => (
          <div key={wi} className={`grid grid-cols-7 ${wi < weeks.length - 1 ? 'border-b border-gray-100' : ''}`}>
            {week.map((day, di) => {
              const ymd         = toYMD(day)
              const isThisMonth = day.getMonth() === month
              const isToday     = ymd === todayStr
              const entries     = dayMap[ymd] || []
              const worst       = entries.length ? worstUrgency(entries) : null
              const isSelected   = ymd === selectedDate
              const estCount     = entries.filter(e => e.isTraxxallEstimate).length
              const exactCount   = entries.length - estCount
              const uniqueAc     = enrolledAircraft.length > 1 ? [...new Set(entries.map(e => e.reg))].length : 0

              return (
                <div
                  key={di}
                  onClick={() => entries.length > 0 ? setSelectedDate(isSelected ? null : ymd) : undefined}
                  className={`
                    relative min-h-[4.5rem] p-1.5 border-r last:border-r-0 border-gray-100 transition-colors
                    ${isThisMonth ? '' : 'opacity-30'}
                    ${worst ? cellBg(worst) : 'hover:bg-gray-50 cursor-default'}
                    ${isSelected ? 'ring-2 ring-inset ring-brand-red/50' : ''}
                  `}
                >
                  {registrations.length === 1 && awayDates.has(ymd) && !flightsByDate[ymd]?.length && (
                    <div
                      className="absolute left-1.5 right-1.5 pointer-events-none"
                      style={{ top: '50%', transform: 'translateY(-50%)', borderTop: '1.5px dashed rgba(0,0,0,0.2)' }}
                    />
                  )}
                  <div className="flex justify-between items-center mb-1">
                    <span className={`text-xs font-semibold w-5 h-5 flex items-center justify-center rounded-full
                      ${isToday ? 'bg-brand-red text-white' : 'text-gray-600'}`}
                    >
                      {day.getDate()}
                    </span>
                    {registrations.length === 1 && flightsByDate[ymd]?.length > 0 && (
                      <div
                        className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: '#101820' }}
                        title={legsTooltip(flightsByDate[ymd], { showReg: registrations.length > 1 })}
                      >
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="#ffffff">
                          <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
                        </svg>
                      </div>
                    )}
                  </div>
                  {entries.length > 0 && (
                    <div className="text-[10px] leading-tight text-gray-600">
                      {exactCount > 0 && (
                        <div className="font-medium">{exactCount} task{exactCount !== 1 ? 's' : ''}</div>
                      )}
                      {estCount > 0 && (
                        <div className="text-gray-400 italic">≈ {estCount}</div>
                      )}
                      {uniqueAc > 0 && (
                        <div className="text-gray-400">{uniqueAc} aircraft</div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
        {[
          { label: 'Tol. expires', bg: 'bg-red-200'    },
          { label: 'In tolerance', bg: 'bg-orange-200' },
          { label: 'Due soon',     bg: 'bg-yellow-200' },
          { label: 'On track',     bg: 'bg-green-200'  },
        ].map(({ label, bg }) => (
          <div key={label} className="flex items-center gap-1.5">
            <span className={`w-3 h-3 rounded-sm ${bg}`} />
            {label}
          </div>
        ))}
      </div>

      {/* Day panel */}
      {selectedDate && dayMap[selectedDate] && (
        <DayPanel
          dateYMD={selectedDate}
          entries={dayMap[selectedDate]}
          viewMode={viewMode}
          onClose={() => setSelectedDate(null)}
          onSelectReg={handleRegSelect}
          discrepItemsByReg={discrepItemsByReg}
          activeProjectsByReg={activeProjectsByReg}
        />
      )}

      {/* Empty states */}
      {enrolledAircraft.length === 0 && (
        <div className="flex items-center justify-center py-24 text-gray-400 text-sm">
          No Traxxall-enrolled aircraft. Mark aircraft as enrolled in the Aircraft Registry.
        </div>
      )}
      {/* Full due list — shown only in single-aircraft view */}
      {viewMode === 'single' && selectedReg && (
        <div className="border-t border-gray-200 pt-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Due List — {selectedReg}</h3>
          <TraxxallDueList
            registration={selectedReg}
            workOrderNumbers={regWoMap[selectedReg] || []}
            onTaskClick={onTaskClick}
            projections={selectedProjKeyMap}
            onAddToSchedule={onAddToSchedule}
          />
        </div>
      )}
    </div>
  )
}
